import { ComponentRef, Injectable } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class ComponentsRelationshipService {
  private readonly instanceToComponentRefMap: Map<
    unknown,
    ComponentRef<unknown>
  > = new Map();
  private readonly childToParentMap: Map<unknown, unknown> = new Map();
  private readonly parentToChildrenMap: Map<unknown, unknown[]> = new Map();

  public registerComponent(componentRef: ComponentRef<unknown>): void {
    this.instanceToComponentRefMap.set(componentRef.instance, componentRef);

    componentRef.onDestroy(() => {
      this.removeComponent(componentRef);
    });
  }

  public registerChildComponent(
    parentComponentRef: ComponentRef<unknown>,
    childComponentRef: ComponentRef<unknown>,
  ) {
    if (!this.instanceToComponentRefMap.has(parentComponentRef.instance)) {
      this.registerComponent(parentComponentRef);
    }

    if (!this.instanceToComponentRefMap.has(childComponentRef.instance)) {
      this.registerComponent(childComponentRef);
    }

    this.childToParentMap.set(
      childComponentRef.instance,
      parentComponentRef.instance,
    );

    childComponentRef.onDestroy(() => {
      this.removeComponent(childComponentRef);
    });

    const parentToChildrenMap = this.parentToChildrenMap.get(
      parentComponentRef.instance,
    );

    this.parentToChildrenMap.set(parentComponentRef.instance, [
      ...(parentToChildrenMap ?? []),
      childComponentRef.instance,
    ]);

    if (!parentToChildrenMap) {
      parentComponentRef.onDestroy(() => {
        this.removeComponent(parentComponentRef);
      });
    }
  }

  public getParent(component: unknown): unknown | undefined {
    return this.childToParentMap.get(component);
  }

  public getChildren(component: unknown): unknown[] | undefined {
    return this.parentToChildrenMap.get(component);
  }

  public getComponentRef(instance: unknown): ComponentRef<unknown> | undefined {
    const componentRef = this.instanceToComponentRefMap.get(instance);
    return componentRef;
  }

  private removeCompenentAsChild(
    childComponentRef: ComponentRef<unknown>,
  ): void {
    if (!this.childToParentMap.has(childComponentRef.instance)) {
      return;
    }

    const parent = this.childToParentMap.get(childComponentRef.instance);
    this.childToParentMap.delete(childComponentRef.instance);
    const childMap = this.parentToChildrenMap.get(parent) as unknown[];
    childMap?.splice(childMap.indexOf(childComponentRef.instance), 1);
  }

  private removeComponentAsParent(
    parentComponentRef: ComponentRef<unknown>,
  ): void {
    if (!this.parentToChildrenMap.has(parentComponentRef.instance)) {
      return;
    }

    const map = this.parentToChildrenMap.get(parentComponentRef.instance);
    this.parentToChildrenMap.delete(parentComponentRef.instance);
    map?.forEach((child) => {
      this.childToParentMap.delete(child);
    });
  }

  private removeComponent(componentRef: ComponentRef<unknown>): void {
    this.removeComponentAsParent(componentRef);
    this.removeCompenentAsChild(componentRef);
    this.instanceToComponentRefMap.delete(componentRef.instance);
  }
}
